import type { DeviceRecord, IdentityRecord, PublicKey } from '@shared/types';
import { deriveIdentityIdFromPublicKey } from '@shared/identityId';
import { authAPI, configAPI } from '../api/client';
import { serverAdminAPI } from '../api/serverAdmin';
import {
  loadAllIdentities,
  loadPendingDeviceRegistrations,
  loadPendingIdentityRegistrations,
  markPendingIdentityRegistrationRegistered,
  type PendingIdentityRegistrationRecord,
  unwrapPendingDeviceRegistrationPrivateKeys,
  unwrapPendingIdentityRegistrationPrivateKey
} from '../crypto/storage';
import { computeFingerprint, createPublicKey, generateEncryptionKeyPair, generateSigningKeyPair } from '../crypto/keys';
import { signDeviceKeyBinding } from '../crypto/deviceKeyBinding';
import { signIdentityRequest, signRequestWithKey } from '../crypto/signing';
import { useAuthStore } from '../store/authStore';
import { getPreferredDeviceLabel, normalizeDeviceLabel } from '../utils/deviceLabel';
import { normalizeServerUrl, serverKeyFromServerUrl } from '../utils/serverKey';
import {
  deletePendingDeviceRegistration,
  deletePendingIdentityRegistration,
  saveDevice,
  saveIdentity,
  savePendingDeviceRegistration,
  savePendingIdentityRegistration
} from './keyStatePersistence';

export type ResumablePendingRegistration = PendingIdentityRegistrationRecord & {
  registeredIdentity?: IdentityRecord;
};

export type PendingIdentityKeyMaterial = {
  record: PendingIdentityRegistrationRecord;
  privateKeyBytes: Uint8Array;
};

export type PendingDeviceKeyMaterial = {
  pendingId: string;
  publicKey: PublicKey;
  encryptionPublicKey: PublicKey;
  privateKeyBytes: Uint8Array;
  encryptionPrivateKeyBytes: Uint8Array;
  label?: string;
};

const fallbackLocks = new Map<string, Promise<void>>();

async function withRegistrationLock<T>(name: string, action: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(name, action);
  }
  const prior = fallbackLocks.get(name) || Promise.resolve();
  let release: () => void = () => {};
  const gate = new Promise<void>((resolve) => {
    release = resolve;
  });
  const next = prior.then(() => gate);
  fallbackLocks.set(name, next);
  await prior;
  try {
    return await action();
  } finally {
    release();
    if (fallbackLocks.get(name) === next) {
      fallbackLocks.delete(name);
    }
  }
}

function isSameServer(left: string, right: string): boolean {
  return (normalizeServerUrl(left) || left) === (normalizeServerUrl(right) || right);
}

export async function getOrCreatePendingCircleIdentityKeyMaterial(
  serverUrl: string,
  flow: PendingIdentityRegistrationRecord['flow'] = 'circle-registration',
  registrationScope = 'circle-registration'
): Promise<PendingIdentityKeyMaterial> {
  // A previous request may have succeeded remotely even if its response was lost locally.
  await loadResumablePendingRegistrations();
  const normalizedServerUrl = normalizeServerUrl(serverUrl) || serverUrl;
  return withRegistrationLock(`circlus:identity-registration:${normalizedServerUrl}:${registrationScope}`, async () => {
    const reusableRecord = (await loadPendingIdentityRegistrations())
      .find((record) => isSameServer(record.serverUrl, normalizedServerUrl) && record.registrationScope === registrationScope);
    if (reusableRecord) {
      return {
        record: reusableRecord,
        privateKeyBytes: await unwrapPendingIdentityRegistrationPrivateKey(reusableRecord)
      };
    }
    if (flow === 'circle-registration') {
      const hasActiveStoredIdentity = Object.values(await loadAllIdentities())
        .some((identity) => (
          !identity.pendingRegistration
          && identity.role !== 'guest'
          && !!identity.serverUrl
          && isSameServer(identity.serverUrl, normalizedServerUrl)
        ));
      const hasActiveRuntimeIdentity = Object.values(useAuthStore.getState().identities)
        .some((identity) => (
          identity.role !== 'guest'
          && !!identity.serverUrl
          && isSameServer(identity.serverUrl, normalizedServerUrl)
        ));
      if (hasActiveStoredIdentity || hasActiveRuntimeIdentity) {
        throw new Error('An identity for this circle already exists on this device');
      }
    }

    const signingKeyPair = await generateSigningKeyPair();
    const publicKey = createPublicKey(signingKeyPair.publicKey, 'ed25519');
    const identityId = await deriveIdentityIdFromPublicKey(publicKey);
    await savePendingIdentityRegistration({
      identityId,
      publicKey,
      privateKeyBytes: signingKeyPair.secretKey,
      serverUrl: normalizedServerUrl,
      flow,
      registrationScope
    });
    const record = (await loadPendingIdentityRegistrations())
      .find((entry) => entry.identityId === identityId);
    if (!record) {
      throw new Error('Failed to persist pending identity key');
    }
    return { record, privateKeyBytes: signingKeyPair.secretKey };
  });
}

export async function getOrCreatePendingDeviceKeyMaterial(
  identityId: string,
  serverUrl: string,
  label?: string
): Promise<PendingDeviceKeyMaterial> {
  return withRegistrationLock(`circlus:device-registration:${identityId}`, async () => {
    const reusableRecord = (await loadPendingDeviceRegistrations(identityId))[0];
    if (reusableRecord) {
      const privateKeys = await unwrapPendingDeviceRegistrationPrivateKeys(reusableRecord);
      return {
        pendingId: reusableRecord.pendingId,
        publicKey: reusableRecord.publicKey,
        encryptionPublicKey: reusableRecord.encryptionPublicKey,
        label: reusableRecord.label,
        ...privateKeys
      };
    }

    const signingKeyPair = await generateSigningKeyPair();
    const encryptionKeyPair = generateEncryptionKeyPair();
    const publicKey = createPublicKey(signingKeyPair.publicKey, 'ed25519');
    const encryptionPublicKey = createPublicKey(encryptionKeyPair.publicKey, 'x25519');
    const pendingId = await savePendingDeviceRegistration({
      identityId,
      publicKey,
      privateKeyBytes: signingKeyPair.secretKey,
      encryptionPublicKey,
      encryptionPrivateKeyBytes: encryptionKeyPair.secretKey,
      serverUrl: normalizeServerUrl(serverUrl) || serverUrl,
      label
    });
    return {
      pendingId,
      publicKey,
      encryptionPublicKey,
      privateKeyBytes: signingKeyPair.secretKey,
      encryptionPrivateKeyBytes: encryptionKeyPair.secretKey,
      label
    };
  });
}

export async function preserveRegisteredIdentityForPendingDeviceActivation(input: {
  identityId: string;
  publicKey: PublicKey;
  privateKeyBytes: Uint8Array;
  serverUrl: string;
  role?: string;
  activationMeta?: PendingIdentityRegistrationRecord['activationMeta'];
}): Promise<void> {
  const normalizedServerUrl = normalizeServerUrl(input.serverUrl) || input.serverUrl;
  const role: IdentityRecord['role'] = (
    input.role === 'owner' || input.role === 'member' || input.role === 'guest'
  ) ? input.role : undefined;
  await withRegistrationLock(`circlus:identity-device-activation:${input.identityId}`, async () => {
    const existing = (await loadPendingIdentityRegistrations())
      .find((record) => record.identityId === input.identityId);
    if (!existing) {
      await savePendingIdentityRegistration({
        identityId: input.identityId,
        publicKey: input.publicKey,
        privateKeyBytes: input.privateKeyBytes,
        serverUrl: normalizedServerUrl,
        flow: 'circle-registration',
        registrationScope: `existing-identity:${input.identityId}`
      });
    }
    await markPendingIdentityRegistrationRegistered(input.identityId, {
      identity: {
        identityId: input.identityId as IdentityRecord['identityId'],
        publicKey: input.publicKey,
        encryptedPrivateKey: null,
        createdAt: new Date().toISOString(),
        status: 'active',
        role
      },
      activationMeta: {
        ...input.activationMeta,
        serverUrl: normalizedServerUrl
      }
    });
  });
}

export async function loadResumablePendingRegistrations(): Promise<ResumablePendingRegistration[]> {
  const records = await loadPendingIdentityRegistrations();
  const reconciled = await Promise.all(records.map(async (record) => {
    if ((record.registrationStatus === 'registered' && record.registeredIdentity) || record.flow === 'direct-guest') {
      return record;
    }
    try {
      const response = await authAPI.getEncryptedIdentityKey(record.publicKey, record.serverUrl);
      if (response.status !== 'ok' || !response.result || response.result.identityId !== record.identityId) {
        return record;
      }
      const identity: IdentityRecord = {
        identityId: response.result.identityId,
        publicKey: response.result.publicKey,
        encryptedPrivateKey: response.result.encryptedPrivateKey || null,
        createdAt: record.createdAt,
        status: 'active',
        role: response.result.role as IdentityRecord['role']
      };
      const activationMeta = {
        ...record.activationMeta,
        serverUrl: record.serverUrl
      };
      await markPendingIdentityRegistrationRegistered(record.identityId, {
        identity,
        activationMeta
      });
      return {
        ...record,
        registrationStatus: 'registered' as const,
        registeredIdentity: identity,
        activationMeta
      };
    } catch {
      return record;
    }
  }));
  return reconciled
    .filter((record): record is ResumablePendingRegistration => (
      (record.flow === 'direct-guest' && !!record.directGuestContext)
      || (record.registrationStatus === 'registered' && !!record.registeredIdentity)
    ))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export async function resumePendingRegistration(identityId: string): Promise<string> {
  const record = (await loadResumablePendingRegistrations()).find((entry) => entry.identityId === identityId);
  if (!record) {
    throw new Error('Pending registration not found');
  }
  if (record.flow === 'direct-guest') {
    throw new Error('Direct guest registration must be resumed from its guest link');
  }
  if (!record.registeredIdentity) {
    throw new Error('Registered identity is missing');
  }

  const serverUrl = normalizeServerUrl(record.serverUrl) || record.serverUrl;
  const identityPrivateKeyBytes = await unwrapPendingIdentityRegistrationPrivateKey(record);
  const pendingDevice = await getOrCreatePendingDeviceKeyMaterial(
    record.identityId,
    serverUrl,
    normalizeDeviceLabel(getPreferredDeviceLabel()) || undefined
  );
  const pendingDeviceId = pendingDevice.pendingId;
  const devicePublicKey = pendingDevice.publicKey;
  const deviceEncryptionPublicKey = pendingDevice.encryptionPublicKey;
  const deviceLabel = pendingDevice.label;
  const devicePrivateKeyBytes = pendingDevice.privateKeyBytes;
  const deviceEncryptionPrivateKeyBytes = pendingDevice.encryptionPrivateKeyBytes;

  const registerRequest = await signIdentityRequest(
    'auth:register-device',
    {
      deviceId: pendingDeviceId,
      devicePublicKey,
      deviceEncryptionPublicKey,
      deviceKeyBinding: await signDeviceKeyBinding({
        identityId: record.identityId,
        devicePublicKey,
        deviceEncryptionPublicKey,
        devicePrivateKey: devicePrivateKeyBytes
      }),
      deviceLabel
    },
    record.identityId,
    identityPrivateKeyBytes
  );
  const deviceResponse = await authAPI.registerDevice(registerRequest, serverUrl);
  if (deviceResponse.status !== 'ok' || !deviceResponse.result) {
    throw new Error(deviceResponse.error?.message || 'Failed to register device');
  }

  const device: DeviceRecord = deviceResponse.result;
  if (device.deviceId !== pendingDeviceId) {
    throw new Error('Server returned an unexpected device ID');
  }
  const deviceCryptoKeys = await saveDevice(
    device.deviceId,
    device.identityId,
    device.publicKey,
    devicePrivateKeyBytes,
    device.label || deviceLabel,
    device.encryptionPublicKey || deviceEncryptionPublicKey,
    deviceEncryptionPrivateKeyBytes
  );

  const activationMeta = record.activationMeta || {};
  const serverId = activationMeta.serverId || serverKeyFromServerUrl(serverUrl) || serverUrl;
  let serverName = activationMeta.serverName || serverId;
  let noNamesOnServer = activationMeta.noNamesOnServer ?? false;
  let isServerAdmin = activationMeta.isServerAdmin ?? false;
  let isPrimaryVpsTarget = activationMeta.isPrimaryVpsTarget ?? false;
  try {
    const signed = await signRequestWithKey('config:server-name', {}, device.deviceId, deviceCryptoKeys.signingKey);
    const response = await configAPI.getServerName(signed, serverUrl);
    if (response.status === 'ok' && response.result?.serverName) {
      serverName = response.result.serverName;
    }
    if (response.status === 'ok' && typeof response.result?.noNamesOnServer === 'boolean') {
      noNamesOnServer = response.result.noNamesOnServer;
    }
  } catch {
    // Saved activation metadata remains a valid fallback while offline metadata refresh can retry later.
  }
  if (record.registeredIdentity.role === 'owner') {
    try {
      const signed = await signRequestWithKey('server-admin:status', {}, device.deviceId, deviceCryptoKeys.signingKey);
      const response = await serverAdminAPI.getStatus(signed, serverUrl);
      if (response.status === 'ok' && response.result?.isServerAdmin) {
        isServerAdmin = true;
        isPrimaryVpsTarget = true;
      }
    } catch {
      // Server admin status can be hydrated later from the server management screen.
    }
  }

  const identityCryptoKeys = await saveIdentity(
    record.identityId,
    record.registeredIdentity.publicKey,
    identityPrivateKeyBytes,
    {
      ...activationMeta,
      serverId,
      serverName,
      serverUrl,
      role: record.registeredIdentity.role,
      accessKind: activationMeta.accessKind,
      accessContextLabel: activationMeta.accessContextLabel,
      publishIdentity: activationMeta.publishIdentity,
      presenceVisible: activationMeta.presenceVisible,
      noNamesOnServer,
      isServerAdmin,
      isPrimaryVpsTarget
    }
  );
  await Promise.all([
    deletePendingIdentityRegistration(record.identityId),
    deletePendingDeviceRegistration(pendingDeviceId)
  ]);

  const state = useAuthStore.getState();
  state.addIdentity({
    identityId: record.identityId,
    serverId,
    serverName,
    serverUrl,
    publicKey: record.registeredIdentity.publicKey,
    fingerprint: activationMeta.fingerprint || await computeFingerprint(record.registeredIdentity.publicKey),
    role: record.registeredIdentity.role,
    accessKind: activationMeta.accessKind,
    accessContextLabel: activationMeta.accessContextLabel,
    identityName: activationMeta.identityName,
    publishIdentity: activationMeta.publishIdentity,
    presenceVisible: activationMeta.presenceVisible,
    noNamesOnServer,
    vpsId: activationMeta.vpsId,
    isServerAdmin,
    isPrimaryVpsTarget,
    ...identityCryptoKeys
  });
  state.addDevice({
    deviceId: device.deviceId,
    identityId: device.identityId,
    publicKey: device.publicKey,
    encryptionPublicKey: device.encryptionPublicKey || deviceEncryptionPublicKey,
    label: device.label || deviceLabel,
    ...deviceCryptoKeys
  });
  state.setCurrentDevice(device.deviceId);
  return record.identityId;
}
